import { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    Button,
    IconButton,
    Checkbox,
    FormControlLabel,
    Switch,
    Autocomplete,
    TextField,
    CircularProgress,
    Paper,
    Chip
} from "@mui/material";
import {
    Close as CloseIcon,
    Straighten as MeasurementsIcon,
    MonitorWeight as WeightIcon,
    CameraAlt as PhotoIcon,
    Description as FormIcon,
    SelectAll as SelectAllIcon,
    SupervisorAccount as TrainerIcon
} from "@mui/icons-material";
import { getClients } from "../../services/TraineeService";
import { getFormTemplates } from "../../services/FormTemplateService";
import { getTrainers } from "../../services/TrainerService";
import { useCompany } from "../../contexts/CompanyContext";
import { useAuth } from "../../contexts/AuthContext";
import { RTL } from "../../cache/CacheRtl";
import { t as coachT } from "../dashboard/coach/coachTokens";

const CHECK_IN_ITEM_TYPES = [
    {
        type: "BODY_MEASUREMENT",
        label: "מדידות גוף",
        description: "הזן את מדידות הגוף שלך",
        icon: <MeasurementsIcon />,
        color: "#8B5CF6"
    },
    {
        type: "BODY_WEIGHT",
        label: "משקל גוף",
        description: "שקול את עצמך ועדכן",
        icon: <WeightIcon />,
        color: "#3B82F6"
    },
    {
        type: "PROGRESS_PHOTO",
        label: "תמונות התקדמות",
        description: "צלם תמונות התקדמות",
        icon: <PhotoIcon />,
        color: "#10B981"
    },
    {
        type: "FORM",
        label: "מילוי טופס",
        description: "מלא שאלון שנשלח אליך",
        icon: <FormIcon />,
        color: "#F59E0B"
    }
];

const CreateCheckInDialog = ({ open, onClose, onSubmit, preSelectedTrainee = null, coach = false }) => {
    const { company } = useCompany();
    const { isAdmin } = useAuth();

    // Only a company admin (and not when a single trainee is pre-selected) may target a whole
    // trainer's roster. Trainers keep the two original modes (all-mine / specific).
    const canTargetByTrainer = isAdmin() && !preSelectedTrainee;

    // Recipient target: "all" (whole company) | "trainer" (all of one trainer's trainees) | "specific".
    const [targetMode, setTargetMode] = useState(preSelectedTrainee ? "specific" : "all");

    const [selectedTrainees, setSelectedTrainees] = useState([]);
    const [traineesOptions, setTraineesOptions] = useState([]);
    const [traineesLoading, setTraineesLoading] = useState(false);

    const [selectedTrainerId, setSelectedTrainerId] = useState(null);
    const [trainerOptions, setTrainerOptions] = useState([]);
    const [trainersLoading, setTrainersLoading] = useState(false);

    const [selectedItems, setSelectedItems] = useState({});
    const [itemRequired, setItemRequired] = useState({});

    const [formTemplates, setFormTemplates] = useState([]);
    const [formTemplatesLoading, setFormTemplatesLoading] = useState(false);
    const [selectedFormTemplateId, setSelectedFormTemplateId] = useState(null);

    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) {
            const hasPre = !!preSelectedTrainee;
            setTargetMode(hasPre ? "specific" : "all");
            setSelectedTrainees(hasPre ? [preSelectedTrainee] : []);
            setSelectedTrainerId(null);
            setSelectedItems({});
            setItemRequired({});
            setSelectedFormTemplateId(null);
            setSubmitting(false);
        }
    }, [open, preSelectedTrainee]);

    useEffect(() => {
        if (open && targetMode === "specific") {
            fetchTrainees();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, targetMode]);

    useEffect(() => {
        if (open && canTargetByTrainer) {
            fetchTrainers();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, canTargetByTrainer]);

    useEffect(() => {
        if (open && selectedItems["FORM"]) {
            fetchFormTemplates();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, selectedItems["FORM"]]);

    const fetchTrainees = async () => {
        setTraineesLoading(true);
        try {
            const res = await getClients(0, 1000, undefined, 0, 'ALL', 'ACTIVE', company || null);
            const list = res.data?.content || res.data || [];
            setTraineesOptions(list.map(t => ({ id: t.id, name: t.name, photoUrl: t.photoUrl, email: t.email, gender: t.gender })));
        } catch {
            setTraineesOptions([]);
        } finally {
            setTraineesLoading(false);
        }
    };

    const fetchTrainers = async () => {
        setTrainersLoading(true);
        try {
            const res = await getTrainers(company?.id);
            const list = res.data || [];
            setTrainerOptions(list.map(tr => ({ id: tr.id, name: tr.name })));
        } catch {
            setTrainerOptions([]);
        } finally {
            setTrainersLoading(false);
        }
    };

    const fetchFormTemplates = async () => {
        setFormTemplatesLoading(true);
        try {
            const res = await getFormTemplates(0, 100, company?.id);
            const templates = res.data?.content || [];
            setFormTemplates(templates.map(t => ({ id: t.id, name: t.name })));
        } catch {
            setFormTemplates([]);
        } finally {
            setFormTemplatesLoading(false);
        }
    };

    const toggleItem = (type) => {
        setSelectedItems(prev => {
            const next = { ...prev };
            if (next[type]) {
                delete next[type];
                if (type === "FORM") setSelectedFormTemplateId(null);
            } else {
                next[type] = true;
            }
            return next;
        });
    };

    const toggleRequired = (type) => {
        setItemRequired(prev => ({ ...prev, [type]: !prev[type] }));
    };

    const hasSelectedItems = Object.keys(selectedItems).length > 0;
    const hasValidForm = !selectedItems["FORM"] || selectedFormTemplateId;
    const hasTrainees =
        targetMode === "all" ||
        (targetMode === "trainer" && !!selectedTrainerId) ||
        (targetMode === "specific" && selectedTrainees.length > 0);
    const canSubmit = hasSelectedItems && hasValidForm && hasTrainees && !submitting;

    const handleSubmit = async () => {
        setSubmitting(true);
        const items = Object.keys(selectedItems).map(type => ({
            type,
            required: !!itemRequired[type],
            formTemplateId: type === "FORM" ? selectedFormTemplateId : null
        }));

        const data = {
            // Whole company → sendToAll; one trainer's roster → trainerId; otherwise explicit ids.
            traineeIds: targetMode === "specific" ? selectedTrainees.map(t => t.id) : [],
            sendToAll: targetMode === "all",
            trainerId: targetMode === "trainer" ? selectedTrainerId : null,
            items
        };

        try {
            await onSubmit(data);
            onClose();
        } catch {
            // error handled by parent
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: coach ? coachT.r : "12px",
                    direction: "rtl"
                }
            }}
        >
            <DialogTitle sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                padding: "20px 24px",
                backgroundColor: coach ? coachT.bg : undefined,
                borderBottom: coach ? `1px solid ${coachT.border}` : "1px solid #E2E8F0"
            }}>
                <Box>
                    <Typography sx={{
                        fontSize: "18px",
                        fontWeight: coach ? 800 : 600,
                        color: coach ? coachT.text : "#1E293B",
                        fontFamily: '"Heebo", "Roboto", sans-serif'
                    }}>
                        שליחת צ'ק-אין
                    </Typography>
                    <Typography sx={{
                        fontSize: "14px",
                        color: "#64748B",
                        marginTop: "4px",
                        fontFamily: '"Heebo", "Roboto", sans-serif'
                    }}>
                        בחר פריטים לבדיקה ושלח למתאמנים
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small" sx={{ color: "#64748B" }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ padding: "24px" }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    {/* Trainee Selection */}
                    <Box>
                        <Typography sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#1E293B",
                            marginBottom: "12px",
                            fontFamily: '"Heebo", "Roboto", sans-serif'
                        }}>
                            שלח אל
                        </Typography>

                        {preSelectedTrainee ? (
                            <Paper
                                variant="outlined"
                                sx={{
                                    padding: "12px 16px",
                                    borderRadius: "8px",
                                    border: "1px solid #E2E8F0",
                                    backgroundColor: "#F8FAFC",
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }}
                            >
                                <Typography sx={{ fontSize: "14px", color: "#64748B", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                                    הצ'ק-אין יישלח אל{" "}
                                    <Typography component="span" sx={{ fontSize: "14px", fontWeight: 600, color: "#1E293B", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                                        {preSelectedTrainee.name}
                                    </Typography>
                                </Typography>
                            </Paper>
                        ) : (
                        <>
                        <Paper
                            variant="outlined"
                            onClick={() => setTargetMode("all")}
                            sx={{
                                padding: "12px 16px",
                                cursor: "pointer",
                                borderRadius: "8px",
                                marginBottom: "8px",
                                border: targetMode === "all" ? "2px solid #57837E" : "1px solid #E2E8F0",
                                backgroundColor: targetMode === "all" ? "rgba(87, 131, 126, 0.04)" : "transparent",
                                transition: "all 0.2s",
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                "&:hover": { borderColor: "#57837E" }
                            }}
                        >
                            <SelectAllIcon sx={{ color: targetMode === "all" ? "#57837E" : "#94A3B8", fontSize: "20px" }} />
                            <Typography sx={{
                                fontSize: "14px",
                                fontWeight: targetMode === "all" ? 600 : 400,
                                color: targetMode === "all" ? "#1E293B" : "#64748B",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}>
                                {canTargetByTrainer ? "כל החברה" : "כל המתאמנים שלי"}
                            </Typography>
                        </Paper>

                        {canTargetByTrainer && (
                        <Paper
                            variant="outlined"
                            onClick={() => setTargetMode("trainer")}
                            sx={{
                                padding: "12px 16px",
                                cursor: "pointer",
                                borderRadius: "8px",
                                marginBottom: "8px",
                                border: targetMode === "trainer" ? "2px solid #57837E" : "1px solid #E2E8F0",
                                backgroundColor: targetMode === "trainer" ? "rgba(87, 131, 126, 0.04)" : "transparent",
                                transition: "all 0.2s",
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                "&:hover": { borderColor: "#57837E" }
                            }}
                        >
                            <TrainerIcon sx={{ color: targetMode === "trainer" ? "#57837E" : "#94A3B8", fontSize: "20px" }} />
                            <Typography sx={{
                                fontSize: "14px",
                                fontWeight: targetMode === "trainer" ? 600 : 400,
                                color: targetMode === "trainer" ? "#1E293B" : "#64748B",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}>
                                כל המתאמנים של מאמן
                            </Typography>
                        </Paper>
                        )}

                        {canTargetByTrainer && targetMode === "trainer" && (
                            <Box sx={{ marginBottom: "8px" }}>
                                <Autocomplete
                                    options={trainerOptions}
                                    loading={trainersLoading}
                                    value={trainerOptions.find(o => o.id === selectedTrainerId) || null}
                                    onChange={(_, newValue) => setSelectedTrainerId(newValue?.id ?? null)}
                                    getOptionLabel={(option) => option?.name || ""}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="בחר מאמן"
                                            size="small"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {trainersLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                )
                                            }}
                                        />
                                    )}
                                />
                            </Box>
                        )}

                        <Paper
                            variant="outlined"
                            onClick={() => setTargetMode("specific")}
                            sx={{
                                padding: "12px 16px",
                                cursor: "pointer",
                                borderRadius: "8px",
                                border: targetMode === "specific" ? "2px solid #57837E" : "1px solid #E2E8F0",
                                backgroundColor: targetMode === "specific" ? "rgba(87, 131, 126, 0.04)" : "transparent",
                                transition: "all 0.2s",
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                "&:hover": { borderColor: "#57837E" }
                            }}
                        >
                            <Typography sx={{
                                fontSize: "14px",
                                fontWeight: targetMode === "specific" ? 600 : 400,
                                color: targetMode === "specific" ? "#1E293B" : "#64748B",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}>
                                מתאמנים ספציפיים
                            </Typography>
                        </Paper>

                        {targetMode === "specific" && (
                            <Box sx={{ marginTop: "12px" }}>
                                <Autocomplete
                                    multiple
                                    options={traineesOptions}
                                    value={selectedTrainees}
                                    onChange={(_, newValue) => setSelectedTrainees(newValue)}
                                    getOptionLabel={(option) => option.name || ""}
                                    filterOptions={(options, { inputValue }) => {
                                        const q = inputValue.toLowerCase();
                                        return options.filter(o =>
                                            (o.name || "").toLowerCase().includes(q) ||
                                            (o.email || "").toLowerCase().includes(q)
                                        );
                                    }}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    loading={traineesLoading}
                                    disabled={!!preSelectedTrainee}
                                    renderOption={(props, option) => (
                                        <Box component="li" {...props} key={option.id} sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            width: "100%",
                                            gap: 1,
                                            direction: "rtl"
                                        }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1E293B" }}>
                                                    {option.name}
                                                </Typography>
                                                {option.gender && (
                                                    <Chip
                                                        label={option.gender === "FEMALE" ? "נ" : option.gender === "MALE" ? "ז" : "-"}
                                                        size="small"
                                                        sx={{
                                                            height: "18px",
                                                            fontSize: "10px",
                                                            fontWeight: 600,
                                                            ...(option.gender === "FEMALE"
                                                                ? { backgroundColor: "#FDF2F8", color: "#BE185D" }
                                                                : option.gender === "MALE"
                                                                    ? { backgroundColor: "#EFF6FF", color: "#1D4ED8" }
                                                                    : { backgroundColor: "#F3F4F6", color: "#6B7280" }),
                                                            "& .MuiChip-label": { padding: "0 4px" }
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                            {option.email && (
                                                <Typography sx={{ fontSize: "12px", color: "#94A3B8" }}>
                                                    {option.email}
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="חפש מתאמנים..."
                                            size="small"
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "8px"
                                                }
                                            }}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {traineesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                    renderTags={(value, getTagProps) => (
                                        <RTL>
                                            {value.map((option, index) => (
                                                <Chip
                                                    {...getTagProps({ index })}
                                                    key={option.id}
                                                    label={option.name}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: "rgba(87, 131, 126, 0.1)",
                                                        color: "#1E293B",
                                                        fontFamily: '"Heebo", "Roboto", sans-serif'
                                                    }}
                                                />
                                            ))}
                                        </RTL>
                                    )}
                                />
                            </Box>
                        )}
                        </>
                        )}
                    </Box>

                    {/* Item Selection */}
                    <Box>
                        <Typography sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#1E293B",
                            marginBottom: "12px",
                            fontFamily: '"Heebo", "Roboto", sans-serif'
                        }}>
                            פריטים לבדיקה
                        </Typography>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            {CHECK_IN_ITEM_TYPES.map((item) => {
                                const isSelected = !!selectedItems[item.type];
                                const isRequired = !!itemRequired[item.type];
                                return (
                                    <Paper
                                        key={item.type}
                                        variant="outlined"
                                        sx={{
                                            padding: "12px 16px",
                                            borderRadius: "8px",
                                            border: isSelected ? "2px solid #57837E" : "1px solid #E2E8F0",
                                            backgroundColor: isSelected ? "rgba(87, 131, 126, 0.04)" : "transparent",
                                            transition: "all 0.2s"
                                        }}
                                    >
                                        <Box sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between"
                                        }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                <Checkbox
                                                    checked={isSelected}
                                                    onChange={() => toggleItem(item.type)}
                                                    sx={{
                                                        padding: 0,
                                                        color: "#CBD5E1",
                                                        "&.Mui-checked": { color: "#57837E" }
                                                    }}
                                                />
                                                <Box sx={{
                                                    width: 36,
                                                    height: 36,
                                                    borderRadius: "8px",
                                                    backgroundColor: `${item.color}15`,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    "& svg": { color: item.color, fontSize: "20px" }
                                                }}>
                                                    {item.icon}
                                                </Box>
                                                <Box>
                                                    <Typography sx={{
                                                        fontSize: "14px",
                                                        fontWeight: 600,
                                                        color: "#1E293B",
                                                        fontFamily: '"Heebo", "Roboto", sans-serif'
                                                    }}>
                                                        {item.label}
                                                    </Typography>
                                                    <Typography sx={{
                                                        fontSize: "12px",
                                                        color: "#64748B",
                                                        fontFamily: '"Heebo", "Roboto", sans-serif'
                                                    }}>
                                                        {item.description}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {isSelected && (
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={isRequired}
                                                            onChange={() => toggleRequired(item.type)}
                                                            size="small"
                                                            sx={{
                                                                "& .MuiSwitch-switchBase.Mui-checked": {
                                                                    color: "#DC2626"
                                                                },
                                                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                                                    backgroundColor: "#DC2626"
                                                                }
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Typography sx={{
                                                            fontSize: "12px",
                                                            color: isRequired ? "#DC2626" : "#94A3B8",
                                                            fontWeight: 600,
                                                            fontFamily: '"Heebo", "Roboto", sans-serif'
                                                        }}>
                                                            {isRequired ? "חובה" : "רשות"}
                                                        </Typography>
                                                    }
                                                    labelPlacement="start"
                                                    sx={{ marginRight: 0 }}
                                                />
                                            )}
                                        </Box>

                                        {/* Form template selector */}
                                        {item.type === "FORM" && isSelected && (
                                            <Box sx={{ marginTop: "12px", paddingRight: "52px" }}>
                                                <Autocomplete
                                                    options={formTemplates}
                                                    value={formTemplates.find(t => t.id === selectedFormTemplateId) || null}
                                                    onChange={(_, newValue) => setSelectedFormTemplateId(newValue?.id || null)}
                                                    getOptionLabel={(option) => option.name || ""}
                                                    loading={formTemplatesLoading}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            placeholder="בחר טופס..."
                                                            size="small"
                                                            sx={{
                                                                "& .MuiOutlinedInput-root": {
                                                                    borderRadius: "8px"
                                                                }
                                                            }}
                                                            InputProps={{
                                                                ...params.InputProps,
                                                                endAdornment: (
                                                                    <>
                                                                        {formTemplatesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                                        {params.InputProps.endAdornment}
                                                                    </>
                                                                ),
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Box>
                                        )}
                                    </Paper>
                                );
                            })}
                        </Box>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{
                padding: "16px 24px",
                borderTop: "1px solid #E2E8F0",
                gap: 2
            }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{
                        flex: 1,
                        borderColor: "#E2E8F0",
                        color: "#64748B",
                        fontFamily: '"Heebo", "Roboto", sans-serif',
                        "&:hover": {
                            borderColor: "#CBD5E1",
                            backgroundColor: "#F8FAFC"
                        }
                    }}
                >
                    ביטול
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!canSubmit}
                    sx={{
                        flex: 1,
                        backgroundColor: coach ? coachT.green : "#57837E",
                        borderRadius: coach ? coachT.rs : undefined,
                        boxShadow: coach ? "none" : undefined,
                        fontFamily: '"Heebo", "Roboto", sans-serif',
                        "&:hover": {
                            backgroundColor: coach ? coachT.greenDark : "#46706B",
                            boxShadow: coach ? "none" : undefined
                        },
                        "&:disabled": {
                            backgroundColor: "#E2E8F0",
                            color: "#94A3B8"
                        }
                    }}
                >
                    {submitting ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : "שלח צ'ק-אין"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateCheckInDialog;
